import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ticketTypeMeta } from "@/lib/ticketType";
import { Req } from "@/components/newTicket/parts";

import { useNewTicketForm } from "@/components/newTicket/useNewTicketForm";
import TicketTypeSection from "@/components/newTicket/sections/TicketTypeSection";
import ContactLookupSection from "@/components/newTicket/sections/ContactLookupSection";
import CustomerInfoSection from "@/components/newTicket/sections/CustomerInfoSection";
import QueryDetailsSection from "@/components/newTicket/sections/QueryDetailsSection";
import PriorityStatusSection from "@/components/newTicket/sections/PriorityStatusSection";
import CustomerConcernSection from "@/components/newTicket/sections/CustomerConcernSection";
import DynamicFieldsSection from "@/components/newTicket/sections/DynamicFieldsSection";
import CustomerConflictModal from "@/components/newTicket/CustomerConflictModal";
import CustomerHistoryPanel from "@/components/CustomerHistoryPanel";

export default function NewTicketDialog({ open, onOpenChange, onCreated, initialType, prefillCustomerId }) {
  const { user } = useAuth();
  const f = useNewTicketForm({ open, initialType, prefillCustomerId, onCreated, onOpenChange });
  const typeMeta = ticketTypeMeta(f.form.ticket_type);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCtx, setHistoryCtx] = useState(null);
  const openHistory = (cc, cn) => { setHistoryCtx({ cc, cn }); setHistoryOpen(true); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setHistoryOpen(false); onOpenChange(v); }}>
      <DialogContent
        className={`${historyOpen ? "max-w-[1350px]" : "max-w-5xl"} max-h-[92vh] p-0 flex flex-col overflow-hidden transition-[max-width] duration-300`}
        data-testid="new-ticket-dialog"
      >
        {/* 6px typed top border (Layer 2) */}
        {typeMeta && <div className={`h-1.5 w-full ${typeMeta.topBar}`} data-testid="type-top-bar" />}

        <DialogHeader className="px-8 pt-6 pb-4 border-b border-slate-200 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="font-heading text-2xl font-semibold text-slate-900">
                Create new ticket
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-1">
                All required fields are marked with <Req />. Auto fields populate from your category selection.
              </p>
            </div>
            {typeMeta && (
              <div className={`shrink-0 inline-flex items-center gap-2 border ${typeMeta.softBorder} ${typeMeta.softBg} rounded-lg px-3 py-2`}
                   data-testid="type-header-chip">
                <div className="relative">
                  <typeMeta.icon className={`w-4 h-4 ${typeMeta.iconColor}`} strokeWidth={2.2} />
                  {typeMeta.pulse && <span className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                </div>
                <div className="leading-tight">
                  <div className={`text-[9px] uppercase tracking-[0.18em] font-bold ${typeMeta.iconColor}`}>Creating</div>
                  <div className="text-xs font-semibold text-slate-900 uppercase tracking-wider">
                    {typeMeta.label} ticket
                  </div>
                </div>
                <span className="text-[9px] text-slate-400 font-mono ml-1">{typeMeta.code_prefix}-####</span>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7">
            <TicketTypeSection
              ticketTypes={f.ticketTypes}
              value={f.form.ticket_type}
              onChange={f.setTicketType}
              typeMeta={typeMeta}
              switchWarn={f.switchWarn}
            />

            <ContactLookupSection
              form={f.form}
              setForm={f.setForm}
              countryCodes={f.countryCodes}
              contactModes={f.contactModes}
              channelsList={f.channelsList}
              languages={f.languages}
              customerMatches={f.customerMatches}
              matchedCustomer={f.matchedCustomer}
              onPickCustomer={f.pickExistingCustomer}
              onUnlinkCustomer={f.unlinkCustomer}
              onViewHistory={openHistory}
              historyOpen={historyOpen}
              validation={f.validation}
            />

            <CustomerInfoSection
              form={f.form}
              setForm={f.setForm}
              ownerName={user?.name}
              validation={f.validation}
            />

            <QueryDetailsSection
              form={f.form}
              setForm={f.setForm}
              mainCats={f.mainCats}
              subCats={f.subCats}
              issueTypes={f.issueTypes}
              autoFields={f.autoFields}
              branches={f.branches}
              validation={f.validation}
            />

            <PriorityStatusSection autoFields={f.autoFields} />

            <DynamicFieldsSection
              fields={f.dynamicFields}
              values={f.customValues}
              setValue={f.setCustomValue}
              errors={f.submitAttempted ? f.errors : {}}
            />

            <CustomerConcernSection
              form={f.form}
              setForm={f.setForm}
              mainName={f.mainName}
              subName={f.subName}
              issueName={f.issueName}
              validation={f.validation}
            />
          </div>

          {historyOpen && historyCtx && (
            <div className="w-[520px] shrink-0 border-l border-slate-200 overflow-hidden bg-slate-50/40" data-testid="new-ticket-history-column">
              <CustomerHistoryPanel countryCode={historyCtx.cc} contactNumber={historyCtx.cn} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 bg-white border-t border-slate-200 px-8 py-4 space-y-3">
          {f.submitAttempted && f.missingFields.length > 0 && (
            <div
              className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3"
              data-testid="validation-summary"
            >
              <div className="text-xs font-semibold text-rose-800 mb-1.5">
                Please complete the following required field{f.missingFields.length === 1 ? "" : "s"}:
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
                {f.missingFields.map((m) => (
                  <li key={m.key} className="text-xs text-rose-700 flex items-center gap-1.5" data-testid={`summary-${m.key}`}>
                    <span className="text-rose-400">•</span> {m.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => { f.reset(); setHistoryOpen(false); onOpenChange(false); }}
              data-testid="new-ticket-cancel"
              className="h-10 px-6"
            >
              Cancel
            </Button>
            <span
              title={
                f.missingFields.length > 0
                  ? `Cannot create ticket. Missing required fields:\n• ${f.missingFields.map((m) => m.label).join("\n• ")}`
                  : undefined
              }
              data-testid="new-ticket-submit-wrap"
            >
              <Button
                onClick={f.submit}
                disabled={f.submitting}
                aria-disabled={Object.keys(f.errors).length > 0}
                data-testid="new-ticket-submit"
                className={`h-10 px-6 text-white ${
                  Object.keys(f.errors).length > 0
                    ? "bg-slate-300 hover:bg-slate-300 cursor-not-allowed"
                    : (typeMeta ? typeMeta.btn : "bg-indigo-600 hover:bg-indigo-700")
                }`}
              >
                {f.submitting ? "Creating..." : (
                  <>
                    {f.confirmSwitch ? "Confirm: " : "Create "}{typeMeta ? `${typeMeta.label} ticket` : "Ticket"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </span>
          </div>
        </div>
      </DialogContent>

      <CustomerConflictModal
        conflictModal={f.conflictModal}
        conflictChoices={f.conflictChoices}
        setConflictChoices={f.setConflictChoices}
        onCancel={f.closeConflictModal}
        onSubmit={f.submit}
      />
    </Dialog>
  );
}
